import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { updateMetaTags } from '../utils/seo'

const SamsungAkkuTauschenPage: React.FC = () => {
  const navigate = useNavigate()
  const go = (path: string) => { navigate(path); setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50) }

  useEffect(() => {
    updateMetaTags({
      title: 'Samsung Akku Tauschen – Alle Modelle & Preise | PhoneMedic',
      description: 'Samsung Akku tauschen ✓ Galaxy S24 / S23 / S22 / A-Serie ✓ ab 69€ ✓ Originale Kapazität ✓ 12 Monate Garantie ✓ Bundesweiter Versand ✓ Express möglich | PhoneMedic',
      keywords: ['Samsung Akku tauschen', 'Samsung Akku wechseln', 'Samsung Akku kaputt', 'Galaxy S24 Akku', 'Galaxy S23 Akku', 'Samsung Batterie tauschen', 'Samsung hält nicht mehr'],
      canonicalUrl: 'https://www.phone-medic.de/samsung-akku-tauschen',
    })

    const schema = [
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: 'Samsung Akku Tauschen',
        description: 'Professioneller Samsung Akku-Tausch für alle Modelle. Originale Kapazität, 12 Monate Garantie, bundesweiter Versand.',
        provider: { '@type': 'LocalBusiness', name: 'PhoneMedic', url: 'https://www.phone-medic.de', telephone: '+491605010907' },
        offers: [
          { '@type': 'Offer', name: 'Samsung Galaxy A34 Akku', price: '69.00', priceCurrency: 'EUR', priceValidUntil: '2026-12-31' },
          { '@type': 'Offer', name: 'Samsung Galaxy S22 Akku', price: '89.00', priceCurrency: 'EUR', priceValidUntil: '2026-12-31' },
          { '@type': 'Offer', name: 'Samsung Galaxy S24 Akku', price: '99.00', priceCurrency: 'EUR', priceValidUntil: '2026-12-31' },
        ],
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '4.9',
          reviewCount: '32',
          bestRating: '5',
        },
        areaServed: 'DE',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'Was kostet ein Samsung Akku-Tausch?',
            acceptedAnswer: { '@type': 'Answer', text: 'Samsung Akku-Tausch ab 69€ für die A-Serie, ab 89€ für die Galaxy S-Serie. Ultra-Modelle etwas teurer. Alle Preise inklusive Einbau und 12 Monate Garantie.' },
          },
          {
            '@type': 'Question',
            name: 'Wann sollte der Samsung Akku getauscht werden?',
            acceptedAnswer: { '@type': 'Answer', text: 'Ein Samsung Akku sollte getauscht werden, wenn er sich nach 2–3 Jahren deutlich schneller entleert als früher, das Gerät unerwartet ausgeht, der Akku aufgebläht ist oder das Gerät beim Laden heiß wird.' },
          },
          {
            '@type': 'Question',
            name: 'Verliere ich Daten beim Samsung Akku-Tausch?',
            acceptedAnswer: { '@type': 'Answer', text: 'Nein. Beim Akku-Tausch wird ausschließlich der Akku ausgewechselt. Alle Daten, Apps und Einstellungen bleiben vollständig erhalten.' },
          },
          {
            '@type': 'Question',
            name: 'Wie lange dauert ein Samsung Akku-Tausch?',
            acceptedAnswer: { '@type': 'Answer', text: 'Der eigentliche Tausch dauert 30–60 Minuten. Inklusive Versandzeit rechne mit 3–5 Werktagen. Express-Service 24h auf Anfrage.' },
          },
          {
            '@type': 'Question',
            name: 'Welche Samsung Modelle werden repariert?',
            acceptedAnswer: { '@type': 'Answer', text: 'Wir tauschen Akkus in allen Samsung Galaxy Modellen – A-Serie, S-Serie (S20 bis S24), Fold- und Flip-Modelle. Auch ältere Modelle auf Anfrage.' },
          },
        ],
      },
    ]

    let script = document.querySelector('#samsung-akku-schema') as HTMLScriptElement
    if (!script) {
      script = document.createElement('script')
      script.id = 'samsung-akku-schema'
      script.type = 'application/ld+json'
      document.head.appendChild(script)
    }
    script.textContent = JSON.stringify(schema)
    return () => { document.querySelector('#samsung-akku-schema')?.remove() }
  }, [])

  const prices = [
    { model: 'Samsung Galaxy A14 / A24', price: 'ab 69 €' },
    { model: 'Samsung Galaxy A34 / A54', price: 'ab 79 €' },
    { model: 'Samsung Galaxy S22 / S22+', price: 'ab 89 €' },
    { model: 'Samsung Galaxy S22 Ultra', price: 'ab 99 €' },
    { model: 'Samsung Galaxy S23 / S23+', price: 'ab 89 €' },
    { model: 'Samsung Galaxy S23 Ultra', price: 'ab 99 €' },
    { model: 'Samsung Galaxy S24 / S24+', price: 'ab 99 €' },
    { model: 'Samsung Galaxy S24 Ultra', price: 'ab 109 €' },
    { model: 'Samsung Galaxy Fold / Flip', price: 'auf Anfrage' },
  ]

  const symptoms = [
    { title: 'Kurze Akkulaufzeit', desc: 'Das Samsung hält keine 4 Stunden mehr durch. Ein deutliches Zeichen für nachlassende Akkukapazität – besonders wenn es früher viel länger hielt.' },
    { title: 'Gerät geht unerwartet aus', desc: 'Bei 20–30 % Ladeanzeige schaltet sich das Samsung aus? Der Akku kann nicht mehr die nötige Spannung liefern.' },
    { title: 'Starke Erhitzung beim Laden', desc: 'Ein stark geschwächter Akku muss mehr arbeiten und erzeugt dabei Wärme. Ungewöhnliche Hitze beim Laden oder normaler Nutzung.' },
    { title: 'Aufgeblähter Akku', desc: 'Wölbt sich das Display nach oben oder lässt sich das Samsung nicht mehr flach auf den Tisch legen? Sofort tauschen lassen.' },
    { title: 'Lädt nicht mehr vollständig', desc: 'Der Akku lädt nur noch bis 80–90 % und hört dann auf – oder bricht beim Entladen früh ein.' },
    { title: 'Langsame Performance', desc: 'Ältere Samsung-Geräte können bei schlechtem Akku gedrosselt werden. Neuer Akku bringt oft deutlich mehr Leistung.' },
  ]

  const faqs = [
    { q: 'Was kostet ein Samsung Akku-Tausch?', a: 'Samsung Akku-Tausch ab 69€ für die A-Serie, ab 89€ für die Galaxy S-Serie. Ultra-Modelle etwas teurer. Alle Preise inklusive Einbau und 12 Monate Garantie.' },
    { q: 'Wann sollte der Akku getauscht werden?', a: 'Wenn das Gerät sich nach 2–3 Jahren deutlich schneller entleert, unerwartet ausgeht, der Akku aufgebläht ist oder das Gerät beim Laden heiß wird.' },
    { q: 'Verliere ich Daten beim Akku-Tausch?', a: 'Nein. Beim Akku-Tausch wird ausschließlich der Akku ausgewechselt. Alle Daten, Apps und Einstellungen bleiben vollständig erhalten.' },
    { q: 'Wie lange dauert ein Samsung Akku-Tausch?', a: 'Der Tausch selbst dauert 30–60 Minuten. Inklusive Versandzeit rechne mit 3–5 Werktagen. Express 24h auf Anfrage.' },
    { q: 'Welche Samsung Modelle werden repariert?', a: 'Wir tauschen Akkus in allen Samsung Galaxy Modellen – A-Serie, S-Serie (S20 bis S24), Fold und Flip. Auch ältere Modelle auf Anfrage.' },
    { q: 'Kann ich den Samsung Akku selbst tauschen?', a: 'Bei modernen Samsung-Geräten sind die Akkus verklebt und sehr schwer zu öffnen. Ein Fehler kann Display oder Platine beschädigen. Professioneller Tausch ist deutlich sicherer.' },
  ]

  return (
    <div className="min-h-screen bg-white">
      <section
        className="relative text-white py-20"
        style={{ backgroundImage: 'url(/hero-bg.webp)', backgroundSize: 'cover', backgroundPosition: 'center top' }}
      >
        <div className="absolute inset-0 bg-gray-900/80" />
        <div className="relative max-w-7xl mx-auto px-4">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-indigo-600/20 border border-indigo-500/30 text-indigo-300 text-sm font-medium px-3 py-1 rounded-full mb-6">
              <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full"></span>
              Alle Modelle · Galaxy S24 bis A-Serie
            </div>
            <h1 className="text-5xl font-bold mb-6 leading-tight">
              Samsung Akku tauschen –<br />
              <span className="text-indigo-400">Alle Modelle & Preise</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl">
              Samsung hält nicht mehr lange oder Akku aufgebläht? Wir tauschen Akkus für alle Galaxy Modelle – professionell, mit 12 Monaten Garantie, bundesweiter Einsende-Service.
            </p>
            <div className="flex flex-wrap gap-4">
              <button onClick={() => go('/repair')} className="bg-indigo-600 text-white px-7 py-3.5 rounded-lg font-semibold hover:bg-indigo-700 transition-colors">
                Modell wählen & Preis prüfen
              </button>
              <a href="mailto:service@phone-medic.de" className="bg-white/10 text-white border border-white/20 px-7 py-3.5 rounded-lg font-semibold hover:bg-white/20 transition-colors">
                Per E-Mail anfragen
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-gray-800 text-white py-5 border-t border-gray-700">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            {[
              { number: 'ab 69 €', label: 'Akkutausch' },
              { number: '30–60 min', label: 'Reparaturzeit' },
              { number: '12 Monate', label: 'Garantie' },
              { number: 'Bundesweit', label: 'Einsende-Service' },
            ].map((s, i) => (
              <div key={i}>
                <div className="text-2xl font-bold text-indigo-400">{s.number}</div>
                <div className="text-sm text-gray-400">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Wann sollte der Akku getauscht werden?</h2>
            <p className="text-gray-500">Diese Symptome zeigen, dass dein Samsung einen neuen Akku braucht</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {symptoms.map((s, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="font-bold text-gray-900 mb-2">{s.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Preise nach Modell</h2>
            <p className="text-gray-500">Alle Preise inklusive Einbau und 12 Monate Garantie</p>
          </div>
          <div className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
            <div className="grid grid-cols-2 bg-gray-800 text-white px-6 py-3 text-sm font-semibold">
              <span>Modell</span>
              <span className="text-right">Preis</span>
            </div>
            {prices.map((p, i) => (
              <div key={i} className={`grid grid-cols-2 px-6 py-4 text-sm border-b border-gray-200 last:border-0 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                <span className="text-gray-700 font-medium">{p.model}</span>
                <span className="text-right font-bold text-indigo-600">{p.price}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-3 text-center">Exakte Preise für dein Modell direkt im Reparatur-Konfigurator.</p>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-10">Häufige Fragen</h2>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">{faq.q}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Weitere Samsung Services</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Samsung Display Reparatur', path: '/samsung-display-reparatur' },
              { label: 'iPhone Akku tauschen', path: '/iphone-akku-tauschen' },
              { label: 'iPhone Display Reparatur', path: '/iphone-display-reparatur' },
              { label: 'Alle Reparaturen', path: '/repair' },
            ].map((item) => (
              <button key={item.path} onClick={() => go(item.path)} className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-sm font-medium text-gray-700 hover:border-indigo-400 hover:text-indigo-700 hover:bg-indigo-50 transition-colors text-center">
                {item.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-3">Samsung Akku tauschen lassen</h2>
          <p className="text-gray-400 mb-8">Alle Modelle · ab 69 € · 12 Monate Garantie · Bundesweiter Einsende-Service</p>
          <div className="flex flex-wrap justify-center gap-4">
            <button onClick={() => go('/repair')} className="bg-indigo-600 text-white px-7 py-3.5 rounded-lg font-semibold hover:bg-indigo-700 transition-colors">
              Modell auswählen & buchen
            </button>
            <a href="mailto:service@phone-medic.de" className="bg-white text-gray-900 px-7 py-3.5 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
              Per E-Mail anfragen
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}

export default SamsungAkkuTauschenPage
